// The CPU half of the terrain pass (plan Part B, §B.4.1-3).
//
// Terrain is immutable for the life of a world, so everything the shader
// cannot afford per pixel is computed once when the hello arrives:
//
//  1. `plane` — ONE RGBA texture at `texelPx` world px. R and G are a
//     Euclidean distance transform, one channel per kind (R trench, G
//     ridge), giving the world-px distance from a point inside a body to
//     its rim. The shader needs it because the smoothed field saturates
//     about one hex in: a contour ring at 0.62 of the depth would sit on a
//     flat and never move. B and A are the smoothed FIELD itself, fT and
//     fR, which the pass used to sum over seven cells at every screen pixel
//     (R432).
//  2. `compR` — the inradius of the connected component each cell belongs
//     to. The ring and feather insets are fractions of the depth, so they
//     need to know how deep the body goes. A trench component too small for
//     a void core stores the NEGATIVE inradius, which is how the shader is
//     told to skip the core.
//
// **The raster is of the smoothed FIELD, not of the raw hex mask.** An
// inward offset of a zigzag keeps the zigzag: an EDT of the hex staircase
// puts a visible 20 px sawtooth on ring 1. Rasterising the same kernel the
// shader uses, at the same radius, and thresholding at 0.5 puts the EDT's
// zero line exactly on the shader's rim.
//
// **The field rides along for free** (R432). This raster already evaluates
// the kernel at every texel centre, because that is what it thresholds; it
// keeps the two fractions it computed instead of throwing them away. The
// terrain field therefore needs no second pass and no render target: the
// four numbers a pixel wants are one bilinear tap of one plane.
//
// This module is pure TypeScript. It touches no WebGL and no DOM, so a test
// can run all of it.

import { BLOCK_RIDGE, BLOCK_TRENCH, COL_PITCH, HEX_SIZE } from '../gen/balance';
import { TERRAIN_VOID_MIN_CELLS } from '../gen/presentation';
import { labelComponents } from '../render/terrain-features';
import { ROW_PITCH, cellAt, cellRowCol, neighborAt } from '../world';
import { TERRAIN_RADIUS_K } from './shaders/prelude';

const SQRT3 = Math.sqrt(3);

/**
 * The default kernel radius, as a multiple of HEX_SIZE. It is the shader's
 * own figure: the raster and the rim must be the same curve.
 */
const DEFAULT_RADIUS_K = TERRAIN_RADIUS_K;

/** The area of one hex cell in world px², 1.5·√3·s². */
export const CELL_AREA = 1.5 * SQRT3 * HEX_SIZE * HEX_SIZE;

/** How many floats one texel of `TerrainPrep.plane` carries. */
export const PLANE_CHANNELS = 4;

/** What `prepareTerrain` hands the terrain pass. */
export interface TerrainPrep {
  /**
   * The terrain plane, RGBA interleaved, `distW * distH * 4` floats:
   *
   *   R — the trench inside-distance in world px (the EDT)
   *   G — the ridge inside-distance in world px
   *   B — fT, the smoothed trench field, 0 to 1
   *   A — fR, the smoothed ridge field, 0 to 1
   *
   * Upload as `FLOAT` into an `RGBA16F` texture with LINEAR filtering and
   * CLAMP wrapping. `RGBA16F` is filterable in core WebGL2, so the plane
   * needs no extension at all — unlike the territory field, which is
   * RENDERED and therefore wants `EXT_color_buffer_float` (R432).
   */
  plane: Float32Array;
  distW: number;
  distH: number;
  /**
   * The world point of texel (0,0)'s low corner, which is `-pad` on both
   * axes. The plane covers the whole rectangle the world quad spans, pad
   * included: a clamped edge texel would put the rim of a trench that
   * touches the world edge in the wrong place (R432).
   */
  loX: number;
  loY: number;
  /** The raster's texel pitch in world px. `uDistUv = 1/(texelPx·size)`. */
  texelPx: number;
  /**
   * One float per cell, row-major `row*cols + col`. The inradius of the
   * cell's component in world px, negative for a trench component under
   * `TERRAIN_VOID_MIN_CELLS`, and 0 for open water. Upload as `FLOAT` into
   * an `R32F` texture with NEAREST filtering; the shader `texelFetch`es it.
   */
  compR: Float32Array;
}

/** Where the terrain plane sits in the world, and how large it is. */
export interface PlaneGeometry {
  /** The world point of texel (0,0)'s low corner: `-pad` on both axes. */
  loX: number;
  loY: number;
  /** The plane's size in texels. */
  distW: number;
  distH: number;
}

/**
 * The plane the raster writes and the engine allocates.
 *
 * ONE function, because two of them cannot agree for ever: when the engine
 * sizes its plane differently from the raster, `TerrainLayer` quietly makes
 * a SECOND plane of its own and the map carries twice the memory with
 * nothing on the screen to say so (R432).
 *
 * The quad reaches `[-pad, cols·COL_PITCH + COL_PITCH/2 + pad]` by
 * `[-pad, worldH + pad]` (`gfx/worldquad.ts`): odd rows stand half a column
 * pitch to the right, and the pad lets a stroke stand outside the world
 * edge. The pitch stays EXACTLY `texelPx` and the plane covers a little
 * more than the quad, because the EDT measures in texels and an anisotropic
 * texel would make it anisotropic too.
 */
export function planeGeometry(
  cols: number, rows: number, texelPx: number, pad: number,
): PlaneGeometry {
  const worldW = cols * COL_PITCH + COL_PITCH / 2 + 2 * pad;
  const worldH = ROW_PITCH * (rows - 1) + 2 * HEX_SIZE + 2 * pad;
  return {
    loX: -pad,
    loY: -pad,
    distW: Math.max(1, Math.ceil(worldW / texelPx)),
    distH: Math.max(1, Math.ceil(worldH / texelPx)),
  };
}

export interface TerrainPrepOptions {
  /** The raster's texel pitch in world px. 4 is the shipping figure. */
  texelPx?: 2 | 4;
  /** The kernel radius in world px. It MUST equal the shader's `uRadiusT`. */
  radiusT?: number;
  /**
   * How far past the world rectangle the plane reaches, in world px. The
   * layer passes the world quad's own pad (`engine.env.pad`), so the plane
   * covers every pixel the quad draws.
   */
  pad?: number;
}

/**
 * The odd-r neighbour table of `world.ts` (`neighborAt`), flat: direction k
 * faces the edge between corners k and k+1, `[(row & 1) * 12 + k * 2]` is
 * dx and the next entry is dy. The raster reads it 12 million times, and a
 * nested array of tuples costs three dereferences where this costs one.
 */
const HEX_OFF_FLAT = new Int8Array([
  1, 0, 0, 1, -1, 1, -1, 0, -1, -1, 0, -1,
  1, 0, 1, 1, 0, 1, -1, 0, 0, -1, 1, -1,
]);

/**
 * The Felzenszwalb–Huttenlocher squared Euclidean distance transform of one
 * row of a 1-D sampled function, in place. `f` is the source, `d` takes the
 * result, and `v` / `z` are the parabola envelope's scratch arrays.
 */
function edt1d(f: Float64Array, d: Float64Array, v: Int32Array, z: Float64Array, n: number): void {
  let k = 0;
  v[0] = 0;
  z[0] = -Infinity;
  z[1] = Infinity;
  for (let q = 1; q < n; q++) {
    let s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
    while (s <= z[k]) {
      k--;
      s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
    }
    k++;
    v[k] = q;
    z[k] = s;
    z[k + 1] = Infinity;
  }
  k = 0;
  for (let q = 0; q < n; q++) {
    while (z[k + 1] < q) k++;
    const dq = q - v[k];
    d[q] = dq * dq + f[v[k]];
  }
}

/**
 * The squared distance from every `true` texel of `inside` to the nearest
 * `false` one, in texel units. Columns first, then rows, which is what makes
 * the two 1-D passes an exact 2-D Euclidean transform.
 */
function edt2d(inside: Uint8Array, w: number, h: number): Float32Array {
  const INF = 1e20;
  const out = new Float32Array(w * h);
  const n = Math.max(w, h);
  const f = new Float64Array(n);
  const d = new Float64Array(n);
  const v = new Int32Array(n);
  const z = new Float64Array(n + 1);

  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) f[y] = inside[y * w + x] !== 0 ? INF : 0;
    edt1d(f, d, v, z, h);
    for (let y = 0; y < h; y++) out[y * w + x] = d[y];
  }
  for (let y = 0; y < h; y++) {
    const base = y * w;
    for (let x = 0; x < w; x++) f[x] = out[base + x];
    edt1d(f, d, v, z, w);
    for (let x = 0; x < w; x++) out[base + x] = d[x];
  }
  return out;
}

/**
 * Fills the enclosed holes of a raster mask: flood "outside" from the border
 * over the texels the mask calls false, then everything the flood missed is
 * inside. Rings pass under a pocket and are clipped by the field instead,
 * which is what `ringRuns` does in the reference (`terrain.ts:631-646`).
 */
function fillHoles(inside: Uint8Array, w: number, h: number): Uint8Array {
  const outside = new Uint8Array(w * h);
  const stack = new Int32Array(w * h);
  let top = 0;
  const push = (i: number) => {
    if (inside[i] === 0 && outside[i] === 0) { outside[i] = 1; stack[top++] = i; }
  };
  for (let x = 0; x < w; x++) { push(x); push((h - 1) * w + x); }
  for (let y = 0; y < h; y++) { push(y * w); push(y * w + w - 1); }
  while (top > 0) {
    const i = stack[--top];
    const x = i % w, y = (i / w) | 0;
    if (x > 0) push(i - 1);
    if (x + 1 < w) push(i + 1);
    if (y > 0) push(i - w);
    if (y + 1 < h) push(i + w);
  }
  const filled = new Uint8Array(w * h);
  for (let i = 0; i < filled.length; i++) filled[i] = outside[i] === 0 ? 1 : 0;
  return filled;
}

/**
 * Gives every enclosed pocket of open water the component of the body that
 * encloses it, so a texel under a filled hole still knows how deep its body
 * is. It is the cell-grid twin of `fillHoles`: flood the not-this-kind cells
 * from the grid border, then a cell the flood missed is a pocket.
 *
 * INVENTED RULE (R431 note): the reference has no such assignment, because
 * it works on traced loops and a hole is a loop of the outer shape. A pocket
 * that touches two components takes the first one BFS reaches, which is the
 * lowest component id. No such pocket exists on a generated map — a pocket
 * enclosed by two separate bodies would mean the two bodies touch, and then
 * they are one component.
 */
function assignPockets(
  blocked: Uint8Array, kind: number, id: Int32Array, cols: number, rows: number,
): void {
  const n = cols * rows;
  const open = new Uint8Array(n);
  const stack = new Int32Array(n);
  let top = 0;
  const push = (i: number) => {
    if (blocked[i] !== kind && open[i] === 0) { open[i] = 1; stack[top++] = i; }
  };
  for (let col = 0; col < cols; col++) { push(col); push((rows - 1) * cols + col); }
  for (let row = 0; row < rows; row++) { push(row * cols); push(row * cols + cols - 1); }
  while (top > 0) {
    const i = stack[--top];
    for (let k = 0; k < 6; k++) {
      const nb = neighborAt(i, k, cols, rows);
      if (nb >= 0) push(nb);
    }
  }
  // A cell of another kind the flood never reached is enclosed. Walk out to
  // the first neighbour of this kind and inherit its component.
  for (let i = 0; i < n; i++) {
    if (blocked[i] === kind || open[i] === 1 || id[i] >= 0) continue;
    for (let k = 0; k < 6; k++) {
      const nb = neighborAt(i, k, cols, rows);
      if (nb >= 0 && id[nb] >= 0) { id[i] = id[nb]; break; }
    }
  }
}

/**
 * Everything the terrain pass needs that a fragment shader cannot afford.
 *
 * `blocked` is the hello's mask: 0 open, `BLOCK_TRENCH`, `BLOCK_RIDGE`.
 * `radiusT` must be the same figure the shader's `uRadiusT` holds, or the
 * EDT's zero line and the shader's rim will not be the same curve.
 */
export function prepareTerrain(
  blocked: Uint8Array, cols: number, rows: number, opts: TerrainPrepOptions = {},
): TerrainPrep {
  const texelPx = opts.texelPx ?? 4;
  const radiusT = opts.radiusT ?? DEFAULT_RADIUS_K * HEX_SIZE;
  const pad = opts.pad ?? 0;
  const { loX, loY, distW, distH } = planeGeometry(cols, rows, texelPx, pad);

  const insideT = new Uint8Array(distW * distH);
  const insideR = new Uint8Array(distW * distH);
  // The plane is allocated here and filled in two halves: the raster writes
  // B and A as it goes, and the EDT writes R and G afterwards. A pair of
  // scratch arrays for the fields would be 14 MB of garbage for nothing.
  const plane = new Float32Array(distW * distH * PLANE_CHANNELS);

  // ---- 1. rasterise the smoothed field at the texel centres -------------
  const invR2 = 1 / (radiusT * radiusT);
  const pitchX = SQRT3 * HEX_SIZE;      // the x pitch of one column
  // The cell under a texel comes from `cellRowCol`, the world's own
  // rounding WITHOUT the grid check, which is what the shader's `cellAt`
  // does: a point outside the grid names the virtual cell that would hold
  // it, and that cell reads as open and unowned. The raster must agree with
  // the shader on the field at the world edge, so it must enumerate the
  // same virtual neighbours. One pair takes the answer of 1.8 million calls.
  const rc: [number, number] = [0, 0];
  for (let j = 0; j < distH; j++) {
    const py = loY + texelPx * j + texelPx / 2;
    const rowBase = j * distW;
    for (let i = 0; i < distW; i++) {
      const px = loX + texelPx * i + texelPx / 2;
      cellRowCol(px, py, rc);
      const row0 = rc[0], col0 = rc[1];
      const off = (row0 & 1) * 12;
      // Most of a map is open water more than one kernel radius from any
      // terrain, and the field there is flat zero. Seven integer reads say
      // so before a single square root is paid for.
      let near = false;
      for (let k = -1; k < 6 && !near; k++) {
        let col = col0, row = row0;
        if (k >= 0) {
          col += HEX_OFF_FLAT[off + k * 2];
          row += HEX_OFF_FLAT[off + k * 2 + 1];
        }
        if (col < 0 || row < 0 || col >= cols || row >= rows) continue;
        if (blocked[row * cols + col] !== 0) near = true;
      }
      if (!near) continue;

      let w = 0, wT = 0, wR = 0;
      for (let k = -1; k < 6; k++) {
        let col = col0, row = row0;
        if (k >= 0) {
          col += HEX_OFF_FLAT[off + k * 2];
          row += HEX_OFF_FLAT[off + k * 2 + 1];
        }
        const dx = px - pitchX * (col + 0.5 * (row & 1) + 0.5);
        const dy = py - (ROW_PITCH * row + HEX_SIZE);
        const u = 1 - (dx * dx + dy * dy) * invR2;
        if (u <= 0) continue;
        const kk = u * u;
        // A cell outside the grid is open water at its virtual centre,
        // exactly as the shader's `kindOf` reads it. It still counts toward
        // the denominator, which is what keeps the rim on the world edge in
        // the same place in both.
        w += kk;
        if (col < 0 || row < 0 || col >= cols || row >= rows) continue;
        const b = blocked[row * cols + col];
        if (b === BLOCK_TRENCH) wT += kk;
        else if (b === BLOCK_RIDGE) wR += kk;
      }
      if (wT + wR <= 0 || w <= 0) continue;
      const halfW = 0.5 * w;
      if (wT >= halfW) insideT[rowBase + i] = 1;
      if (wR >= halfW) insideR[rowBase + i] = 1;
      // The two fractions the threshold was taken on ARE the shader's
      // field. Keeping them is two stores; recomputing them per screen
      // pixel is seven kernels and seven texel fetches (R432).
      const t = (rowBase + i) * PLANE_CHANNELS;
      plane[t + 2] = wT / w;
      plane[t + 3] = wR / w;
    }
  }

  const filledT = fillHoles(insideT, distW, distH);
  const filledR = fillHoles(insideR, distW, distH);
  const d2T = edt2d(filledT, distW, distH);
  const d2R = edt2d(filledR, distW, distH);

  // ---- 2. the two distance channels ------------------------------------
  // The EDT measures to the nearest OUTSIDE texel centre, so the rim sits
  // half a texel short of it. Subtracting half a texel puts the zero line on
  // the rim, which is where the shader's field also reads zero.
  const half = texelPx / 2;
  for (let i = 0; i < d2T.length; i++) {
    const t = i * PLANE_CHANNELS;
    plane[t] = Math.max(0, texelPx * Math.sqrt(d2T[i]) - half);
    plane[t + 1] = Math.max(0, texelPx * Math.sqrt(d2R[i]) - half);
  }

  // ---- 3. components and their inradius --------------------------------
  // The walk lives in the leaf `render/terrain-features.ts`, because the
  // chart reads the same bodies this prep measures: one owner, so the
  // shader's `compR` and the chart's feature list can never disagree about
  // what one body is (R548).
  const trench = labelComponents(blocked, BLOCK_TRENCH, cols, rows);
  const ridge = labelComponents(blocked, BLOCK_RIDGE, cols, rows);
  assignPockets(blocked, BLOCK_TRENCH, trench.label, cols, rows);
  assignPockets(blocked, BLOCK_RIDGE, ridge.label, cols, rows);

  const radT = new Float64Array(trench.sizes.length);
  const radR = new Float64Array(ridge.sizes.length);
  const areaT = new Float64Array(trench.sizes.length);
  const texelArea = texelPx * texelPx;

  for (let j = 0; j < distH; j++) {
    const py = loY + texelPx * j + texelPx / 2;
    const rowBase = j * distW;
    for (let i = 0; i < distW; i++) {
      const t = rowBase + i;
      const hasT = filledT[t] !== 0, hasR = filledR[t] !== 0;
      if (!hasT && !hasR) continue;
      const c = cellAt(loX + texelPx * i + texelPx / 2, py, cols, rows);
      if (c < 0) continue;
      if (hasT) {
        const id = trench.label[c];
        if (id >= 0) {
          areaT[id] += texelArea;
          const d = plane[t * PLANE_CHANNELS];
          if (d > radT[id]) radT[id] = d;
        }
      }
      if (hasR) {
        const id = ridge.label[c];
        if (id >= 0) {
          const d = plane[t * PLANE_CHANNELS + 1];
          if (d > radR[id]) radR[id] = d;
        }
      }
    }
  }

  // A trench too small for a void core stores its inradius NEGATED: the
  // shader reads `compR <= 0` as "no core here" and still has the figure if
  // a later pass wants it.
  const minArea = TERRAIN_VOID_MIN_CELLS * CELL_AREA;
  const compR = new Float32Array(cols * rows);
  for (let i = 0; i < compR.length; i++) {
    const b = blocked[i];
    if (b === BLOCK_TRENCH) {
      const id = trench.label[i];
      if (id >= 0) compR[i] = areaT[id] < minArea ? -radT[id] : radT[id];
    } else if (b === BLOCK_RIDGE) {
      const id = ridge.label[i];
      if (id >= 0) compR[i] = radR[id];
    }
  }

  return { plane, distW, distH, loX, loY, texelPx, compR };
}
